import { Trans, useTranslation } from "react-i18next";
import { generatePath, Link } from "react-router-dom";
import { AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { PATH_AGREEMENT } from "@/constants";
import { TaxStatusChangedInfo } from "@/schemas/agreement";

interface AgreementTaxStatusChangedDialogProps {
  info: TaxStatusChangedInfo;
  open: boolean;
  onClose: () => void;
}

export const AgreementTaxStatusChangedDialog = ({
  info,
  open,
  onClose,
}: AgreementTaxStatusChangedDialogProps) => {
  const { t } = useTranslation();

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="sm:max-w-[560px] [&>button:last-child]:hidden">
        <DialogHeader className="space-y-6">
          <div className="flex items-center gap-3">
            <AlertTriangle className="h-5 w-5 text-yellow-500 flex-shrink-0" />
            <DialogTitle>
              {t("agreements.taxStatusChangedDialog.title")}
            </DialogTitle>
          </div>
          <DialogDescription>
            <div className="space-y-4 text-base text-foreground">
              <p>
                <Trans
                  i18nKey="agreements.taxStatusChangedDialog.parallelAgreementMessage"
                  values={{
                    refNumber: info.parallel_agreement_ref_number,
                    parallelTaxStatus: t(
                      `taxStatus.${info.parallel_agreement_tax_status}`,
                    ),
                  }}
                  components={[
                    <Link
                      key="link"
                      to={generatePath(PATH_AGREEMENT, {
                        agreementId: info.parallel_agreement_id,
                      })}
                      target="_blank"
                      rel="noreferrer"
                      className="font-semibold text-primary underline underline-offset-4"
                    />,
                    <span key="bold" className="font-semibold" />,
                  ]}
                />
              </p>
              <p>
                <Trans
                  i18nKey="agreements.taxStatusChangedDialog.currentTaxStatusMessage"
                  values={{
                    currentTaxStatus: t(`taxStatus.${info.new_tax_status}`),
                  }}
                  components={[<span key="bold" className="font-semibold" />]}
                />
              </p>
            </div>
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button className="w-full sm:w-auto" onClick={onClose}>
            {t("agreements.taxStatusChangedDialog.confirm")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
